import type { PetChatbot, PetChatTime, PetMessage } from '.';
import type { Pet, PetState } from '../pet';
import type { ProfileSlice } from '../../profile';
import type { VisitRecord } from '../../visit/record';
import type { Inventory } from '../inventory';
import type { Level } from '../level';
import type { History } from '../history';

import { createScheduledActions, SB } from '@play-co/replicant';
import { duration } from '../../utils/duration';
import { formatPSTTime, getLocalTimezone } from '../../utils/time';
import { extractScheduledActionHandlers } from '../../scheduledActions/utils';
import { encyclopediaSchema } from '../../encyclopedia/encyclopedia.schema';
import { userSettingsSchema } from '../../state/settings';
import { Feature } from 'src/replicant/analytics';
import fateSchema from '../fate/fate.schema';
import {
  getChatbotStaticMessage,
  sendChatbotMessage,
  generateReplicantChatbotPayload,
} from 'src/replicant/chatbot/chatbotMessage';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import ab from 'src/replicant/ruleset/ab';
import { encodeQuickReplyPayload } from 'src/replicant/chatbot/chatbot.utils';
import { getConversationalImageId } from 'src/replicant/chatbot/chatbotConversation';
import Script from 'src/replicant/airtable/Script';

export const petChatbotScheduledActions = (
  PetChatbot: Pick<PetChatbot, 'ruleset'>,
  Pet: Pet,
  Profile: ProfileSlice,
  VisitRecord: VisitRecord,
  Inventory: Inventory,
  Level: Level,
  History: History,
) => {
  const stateSchema = SB.object({
    ...Pet.schema,
    ...Profile.schema,
    ...VisitRecord.schema,
    ...Inventory.schema,
    ...Level.schema,
    ...History.schema,
    ...encyclopediaSchema,
    settings: userSettingsSchema,
    ...fateSchema,
    platform: SB.string().optional(),
    entryFinalCount: SB.number().optional(),
  });
  const apiSchema = {
    sendStatusChangedChatbotMessage: SB.object({
      status: SB.tuple(Pet.ruleset.petStates),
      userId: SB.string().optional(),

      // Deprecated
      locale: SB.tuple(['en', 'ja']).optional(),
    }),
    sendPetChatMessage: SB.tuple(['morning', 'lunch', 'evening']),
    sendDailyMessage: SB.object({ day: SB.number() }),
    sendTestingMessage: SB.object({
      index: SB.number(),
      title: SB.string(),
      subFeature: SB.string().optional(),
    }),
    sendQuizEggChatMessage: SB.object({ index: SB.number().optional() }),
  };

  const scheduledActions = createScheduledActions(
    stateSchema,
    apiSchema,
  )({
    sendStatusChangedChatbotMessage: async (
      state,
      { status, userId, locale },
      api,
    ) => {
      if (locale) {
        api.sendAnalyticsEvents([
          {
            eventType: 'DebugDeprecatedReference',
            eventProperties: {
              name: 'locale',
              context: 'sendStatusChangedChatbotMessage',
            },
          },
        ]);
      }

      // Default to player if target user is not specified
      if (!userId) {
        userId = api.getUserID();
      }

      if (userId === api.getUserID()) {
        const { wasVisitedSinceLastSession } = VisitRecord.getters;
        if (wasVisitedSinceLastSession(state)) {
          // Visitor will be sending status messages to us until we login again
          return;
        }
      } else {
        const states = await api.fetchStates([userId]);
        if (!states[userId]) {
          // Can't find state for target player
          return;
        }

        const { state: friendState } = states[userId];
        const { getLastVisitTimestamp } = VisitRecord.getters;
        if (friendState.updatedAt > getLastVisitTimestamp(state, userId)) {
          // Target player has logged on since out last visit, so stop sending messages for them
          return;
        }

        // Use friend state to determine message content
        state = friendState;
      }

      // Schedule the next status update message
      const now = api.date.now();
      scheduleStatusUpdateMessage(
        {
          nextPetState: Pet.getters(state).getNextPetState(now),
          now,
        },
        api,
        userId,
      );

      // Don't send a message if it's disallowed by ruleset logic
      const { canSendPetStatusNotification } = PetChatbot.ruleset;
      const { receiveMessages } = state.settings;
      const shouldSendMessage = canSendPetStatusNotification(
        receiveMessages,
        status,
      );
      if (!shouldSendMessage) {
        return;
      }

      const {
        getCurrentEvolution,
        getAnalyticsUserProperties,
        getAnalyticsAttributeUserProperties,
      } = Pet.getters(state);
      const payload = generateReplicantChatbotPayload({
        feature: Feature.STATUS._,
        $subFeature: `${Feature.STATUS._}_${status}`,
      });
      const analyticsUserProperties = {
        ...getAnalyticsUserProperties(now, false),
        ...getAnalyticsAttributeUserProperties(now),
      };
      const species = getCurrentEvolution().species || 'ball';

      // LINE Chatbot Message
      // const message = getChatbotFlexMessage({
      //   template: 'status',
      //   payload,
      //   data: {
      //     locale: getLocale(state),
      //     species: getCurrentEvolution().species,
      //     petName: state.pet.name,
      //     status,
      //   },
      //   analyticsUserProperties,
      // });
      // api.chatbot.sendMessage(userId, message);

      const hungry = status === 'hungry';
      const bucket =
        state?.ruleset?.abTests?.[ab.tests.TEST_CONVERSATIONAL_CHATBOT]
          ?.bucketId;

      if (hungry && bucket === 'generic') {
        // === CONVERSATION - HUNGRY - GENERIC ================================
        const imageId = getConversationalImageId(species, 'feed');
        const quickReply = await getChatbotStaticMessage({
          state,
          api,
          template: 'messengerQuickReplyButtons',
          title: 'Will you feed me?',
          imageUrl: imageId,
          payload: {
            $channel: 'UPDATE',
            feature: 'status',
            $subFeature: 'conversation-hungry-generic',
            $creativeAssetID: imageId,
          },
          quickReplyButtons: [
            {
              title: "I'll feed you!",
              type: 'postback',
              payload: encodeQuickReplyPayload({
                messageText: 'Yay! come on over!',
                buttonText: 'Play Trembly!',
                imageUrl: getConversationalImageId(species, 'yes'),
                species,
              }),
            },
            {
              title: 'No food for you!',
              type: 'postback',
              payload: encodeQuickReplyPayload({
                messageText:
                  "You're being mean! I'll forgive you if you come play!",
                buttonText: 'Play Trembly!',
                imageUrl: getConversationalImageId(species, 'no'),
                species,
              }),
            },
          ],
        });
        await sendChatbotMessage(api, userId, quickReply, state.platform);
      } else if (hungry && bucket === 'actioned') {
        // === CONVERSATION - HUNGRY - ACTIONED ===============================
        const imageId = getConversationalImageId(species, 'feed');
        const quickReply = await getChatbotStaticMessage({
          state,
          api,
          template: 'messengerQuickReplyButtons',
          title: 'Will you feed me?',
          imageUrl: getConversationalImageId(species, 'feed'),
          payload: {
            $channel: 'UPDATE',
            feature: 'status',
            $subFeature: 'conversation-hungry-actioned',
            $creativeAssetID: imageId,
          },
          quickReplyButtons: [
            {
              title: 'Vibrating Food [40]',
              type: 'postback',
              payload: encodeQuickReplyPayload({
                messageText: "Thanks for the meal!\nNow let's play!",
                buttonText: 'Play Trembly!',
                action: 'pay-food-40',
                imageUrl: getConversationalImageId(species, 'thanks'),
                species,
              }),
            },
            {
              title: 'Gentle Sushi [60]',
              type: 'postback',
              payload: encodeQuickReplyPayload({
                messageText: "Thanks for the meal!\nNow let's play!",
                buttonText: 'Play Trembly!',
                action: 'pay-food-60',
                imageUrl: getConversationalImageId(species, 'thanks'),
                species,
              }),
            },
            {
              title: 'No food for you!',
              type: 'postback',
              payload: encodeQuickReplyPayload({
                messageText:
                  "You're being mean! I'll forgive you if you come play!",
                buttonText: 'Play Trembly!',
                imageUrl: getConversationalImageId(species, 'no'),
                species,
              }),
            },
          ],
        });
        await sendChatbotMessage(api, userId, quickReply, state.platform);
      } else {
        // Regular chatbot message
        const imageUrl = `static/${species}/${status}`;
        payload.$creativeAssetID = imageUrl;
        const message = await getChatbotStaticMessage({
          state,
          api,
          title: getLocaleConfig(locale).status[status].text,
          imageUrl,
          payload,
          analyticsUserProperties,
        });
        void sendChatbotMessage(api, userId, message, state.platform);
      }
    },

    sendPetChatMessage: async (state, args, api) => {
      const now = api.date.now();
      if (now - state.updatedAt > duration({ days: 1 })) {
        // Stop sending messages 1 day after last login.
        return;
      }

      // Only send chat messages if notifications are fully enabled.
      if (state.settings.receiveMessages === 'on') {
        const {
          getCurrentEvolution,
          getAnalyticsUserProperties,
          getAnalyticsAttributeUserProperties,
        } = Pet.getters(state);
        const { species } = getCurrentEvolution();
        const speciesMessages: PetMessage[] =
          PetChatbot.ruleset.petSpeciesMessages[species];
        const validMessages = speciesMessages.filter(
          ({ time }) => time === args,
        );
        const message =
          validMessages[Math.floor(api.math.random() * validMessages.length)];

        for (let i = 1; i <= 3; ++i) {
          const messageProp = `text_${i}_${
            state.settings.locale === 'en' ? 'en' : 'jp'
          }`;

          const messageText = message[messageProp];
          if (messageText) {
            const payload = generateReplicantChatbotPayload({
              feature: Feature.GREETING._,
              $subFeature: args,
            });

            const analyticsUserProperties = {
              ...getAnalyticsUserProperties(now, false),
              ...getAnalyticsAttributeUserProperties(now),
            };

            // LINE Chatbot Message
            // const message = chatbotMessageTemplates.petChat({
            //   args: {
            //     species,
            //     petName: state.pet.name,
            //     message: substituteTokens(messageText, {
            //       pet_name: state.pet.name,
            //       player_name: state.profile.name,
            //     }),
            //     url,
            //   },
            //   payload,
            //   analyticsUserProperties,
            // });
            // api.chatbot.sendMessage(api.getUserID(), message);

            // WEB Chatbot Message
            /*
            // Do not send these messages for now, no static images available yet
            const assetKey = getPetAssetKey(species);
            const imageUrl = speciesAssets[assetKey];
            const message = await getChatbotStaticMessage({
              state,
              api,
              imageUrl,
              title: substituteTokens(messageText, {
                pet_name: state.pet.name,
                player_name: state.profile.name,
              }),
              payload,
              analyticsUserProperties,
            });
            sendChatbotMessage(api, api.getUserID(), message, state.platform);
            */
          }
        }
      }

      const timeZone = getLocalTimezone(state);
      const nextMessage = PetChatbot.ruleset.getNextChatMessageSchedule(
        now,
        api.math.random(),
        timeZone,
      );
      schedulePetChatMessage(nextMessage, now, api);
    },

    sendDailyMessage: async (state, args, api) => {
      const now = api.date.now();

      // Schedule next message until day 7
      if (args.day < 7) {
        scheduleDailyMessage({ now, day: args.day + 1 }, api);
      }

      const {
        getCurrentEvolution,
        getAnalyticsUserProperties,
        getAnalyticsAttributeUserProperties,
      } = Pet.getters(state);

      const payload = generateReplicantChatbotPayload({
        feature: 'DAILY_MESSAGE',
        $subFeature: `DAY_${args.day}`,
      });

      const analyticsUserProperties = {
        ...getAnalyticsUserProperties(now, false),
        ...getAnalyticsAttributeUserProperties(now),
      };
      const species = getCurrentEvolution()?.species || 'ball';
      const imageUrl = `static/${species}/lonely`;
      const message = await getChatbotStaticMessage({
        state,
        api,
        title: 'We miss you!',
        imageUrl,
        payload,
        analyticsUserProperties,
      });
      void sendChatbotMessage(api, state.id, message, state.platform);
    },

    sendTestingMessage: async (state, args, api) => {
      const now = api.date.now();

      const {
        getCurrentEvolution,
        getAnalyticsUserProperties,
        getAnalyticsAttributeUserProperties,
      } = Pet.getters(state);

      const prefix = args.subFeature ? `${args.subFeature}_` : '';
      const payload = generateReplicantChatbotPayload({
        feature: 'TESTING_MESSAGE',
        $subFeature: `${prefix}INDEX_${args.index}`,
      });

      const analyticsUserProperties = {
        ...getAnalyticsUserProperties(now, false),
        ...getAnalyticsAttributeUserProperties(now),
      };
      const species = getCurrentEvolution()?.species || 'ball';
      const imageUrl = `static/${species}/very_lonely`;
      const message = await getChatbotStaticMessage({
        state,
        api,
        title: args.title,
        imageUrl,
        payload,
        analyticsUserProperties,
      });
      void sendChatbotMessage(api, state.id, message, state.platform);
    },

    sendQuizEggChatMessage: async (state, args, api) => {
      const index = args.index ?? -1;
      const quiz = getQuizEggChatData(index);
      const imageId = 'static/generic';

      // Combine the first two answers into a single title...
      const answerTitle = quiz.answers[0] + ' ' + quiz.answers[1];
      // ...and the third answer into a subtitle
      const answerSubtitle = quiz.answers[2];

      // Convert each quiz option into a quick reply button
      const quickReplyButtons = quiz.options.map((option) => {
        return {
          title: option,
          type: 'postback',
          payload: encodeQuickReplyPayload({
            messageText: answerTitle,
            messageSubtitle: answerSubtitle,
            buttonText: 'Play Trembly!',
          }),
        };
      });

      const message = await getChatbotStaticMessage({
        state,
        api,
        template: 'messengerQuickReplyButtons',
        title: quiz.title,
        imageUrl: imageId,
        payload: {
          $channel: 'UPDATE',
          feature: 'quiz_egg_chat',
          $subFeature: quiz.id,
          $creativeAssetID: imageId,
        },
        quickReplyButtons,
      });
      void sendChatbotMessage(api, state.id, message, state.platform);

      // Schedule another quiz message after 23 hours if user hasn't reached the entryFinal yet
      if (!state.entryFinalCount) {
        scheduleQuizEggChat(api, index + 1);
      }
    },
  });

  return {
    schema: apiSchema,
    actions: extractScheduledActionHandlers(scheduledActions),
  };
};

export function schedulePetChatMessage(
  message: { period: PetChatTime; delayInMS: number },
  now: number,
  api,
) {
  const { period, delayInMS } = message;
  api.sendAnalyticsEvents([
    {
      eventType: 'DebugChatbotMessageSchedule',
      eventProperties: {
        feature: Feature.GREETING._,
        $subFeature: period,
        time: formatPSTTime(now + delayInMS - api.getClockOffset()),
      },
    },
  ]);
  api.scheduledActions.schedule.sendPetChatMessage({
    args: period,
    notificationId: 'sendPetChatMessage',
    delayInMS,
  });
}

type StatusUpdateScheduleOpts = {
  nextPetState: { state: PetState; timestamp: number };
  now: number;
};

export function scheduleStatusUpdateMessage(
  { nextPetState, now }: StatusUpdateScheduleOpts,
  api,
  userId: string = api.getUserID(),
) {
  if (!nextPetState) {
    return;
  }

  const { state, timestamp } = nextPetState;
  api.sendAnalyticsEvents([
    {
      userId,
      eventType: 'DebugPetStateSchedule',
      eventProperties: {
        state,
        time: formatPSTTime(timestamp - api.getClockOffset()),
        withoutOffset: formatPSTTime(timestamp),
      },
    },
  ]);

  // Schedule status changed messages separately for each friend
  let notificationId = 'sendStatusChangedChatbotMessage';
  if (userId !== api.getUserID()) {
    notificationId += `#${userId}`;
  }

  api.scheduledActions.schedule.sendStatusChangedChatbotMessage({
    args: {
      status: state,
      userId,
    },
    notificationId,
    delayInMS: timestamp - now,
  });
}

type DailyMessageScheduleOpts = {
  now: number;
  day: number;
};

export function scheduleDailyMessage(args: DailyMessageScheduleOpts, api) {
  // Only schedule daily messages for the first week
  if (args.day > 7) return;

  // Notification id, cleared up on loggin in and re-scheduled on game end
  const notificationId = 'sendDailyMessage';

  // Set tomorrow date at 7am
  const today = new Date(args.now);
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(7);
  tomorrow.setMinutes(0);
  tomorrow.setSeconds(0);

  // Calculate delay in milliseconds until tomorrow 7am
  const delayInMs = tomorrow.getTime() - args.now;

  // Schedule the daily message - once fired it will reschedule a new one for the next day, until day 7
  api.scheduledActions.schedule.sendDailyMessage({
    args: { day: args.day },
    notificationId,
    delayInMS: delayInMs,
  });

  // Send debug analytics event to check if messages are being scheduled correctly
  const userId = api.getUserID();
  api.sendAnalyticsEvents([
    {
      userId,
      eventType: 'DebugDailyMessageSchedule',
      eventProperties: {
        day: args.day,
        scheduleDay: today.toString(),
        scheduledTo: tomorrow.toString(),
      },
    },
  ]);
}

type TestingMessageScheduleOpts = {
  now: number;
};

export function scheduleTestingMessages(
  args: TestingMessageScheduleOpts,
  subFeature: string,
  api,
) {
  // Schedule the first testing message
  const delay1 = duration({ hours: 22 });
  api.scheduledActions.schedule.sendTestingMessage({
    args: { index: 1, title: 'We miss you!', subFeature },
    notificationId: 'sendTestingMessage1',
    delayInMS: delay1,
  });

  // Schedule the second testing message
  const delay2 = duration({ hours: 23 });
  api.scheduledActions.schedule.sendTestingMessage({
    args: { index: 2, title: 'We miss you!!', subFeature },
    notificationId: 'sendTestingMessage2',
    delayInMS: delay2,
  });

  // Schedule the third testing message
  const delay3 = duration({ hours: 23, minutes: 45 });
  api.scheduledActions.schedule.sendTestingMessage({
    args: { index: 3, title: 'We miss you!!!', subFeature },
    notificationId: 'sendTestingMessage3',
    delayInMS: delay3,
  });

  // Send debug analytics event to check if messages are being scheduled correctly
  const userId = api.getUserID();
  api.sendAnalyticsEvents([
    {
      userId,
      eventType: 'DebugTestingMessageSchedule',
      eventProperties: {
        subFeature,
        scheduleTime: new Date(args.now).toString(),
        shceduled1: new Date(args.now + delay1).toString(),
        shceduled2: new Date(args.now + delay2).toString(),
        shceduled3: new Date(args.now + delay3).toString(),
      },
    },
  ]);
}

export function unscheduleTestingMessages(api) {
  api.scheduledActions.unschedule('sendTestingMessage1');
  api.scheduledActions.unschedule('sendTestingMessage2');
  api.scheduledActions.unschedule('sendTestingMessage3');
}

function substituteTokens(template: string, substitutions: {}) {
  let text = template;
  for (const token in substitutions) {
    text = text.replace('{' + token + '}', substitutions[token]);
  }
  return text;
}

const quizEggChatScriptIds = [
  'ball_008_01', // Football - OK
  'crab_037_01', // Soy Sauce - OK
  'crab_038_01', // Diabetics Drink - OK
  'crab_039_01', // Coke - - OK
  'crab_040_01', // Foor For Memory - OK
  'crab_041_01', // Bath Boost Immunity - OK
  'crab_061_01', // Fix Numb Foot - OK
  'crab_062_01', // Sweat While Sleep - OK
  'dog_018_01', // House While Thunder - OK
  'starball_010_01', // Constellation - OK
];

export function scheduleQuizEggChat(api, index = -1) {
  // Randomize the index if not specified
  if (index < 0) {
    index = Math.floor(Math.random() * quizEggChatScriptIds.length);
  }

  const now = api.date.now();
  const delay = duration({ hours: 23 });
  api.scheduledActions.schedule.sendQuizEggChatMessage({
    args: { index },
    notificationId: 'sendQuizEggChatMessage',
    delayInMS: delay,
  });

  const userId = api.getUserID();
  api.sendAnalyticsEvents([
    {
      userId,
      eventType: 'DebugQuizEggChatSchedule',
      eventProperties: {
        scheduleTime: new Date(now).toString(),
        shceduleTo: new Date(now + delay).toString(),
      },
    },
  ]);
}

export function unscheduleQuizEggChat(api) {
  const userId = api.getUserID();
  api.sendAnalyticsEvents([
    {
      userId,
      eventType: 'DebugQuizEggChatScheduleCancel',
      eventProperties: {
        cancelTime: new Date(api.date.now()).toString(),
      },
    },
  ]);
  api.scheduledActions.unschedule('sendQuizEggChatMessage');
}

export function getQuizEggChatData(index = -1) {
  // Randomize the id if index has not been specified
  if (index < 0)
    index = Math.floor(Math.random() * quizEggChatScriptIds.length);

  // Make sure index is within the range of the scriptsIds array
  index = index % quizEggChatScriptIds.length;

  const id = quizEggChatScriptIds[index];
  const questionItem = Script.find((item) => item.id === id);
  const title = questionItem['text_en_alt'] ?? questionItem.text_en;
  const options: string[] = [];
  const answers: string[] = [];
  const maxOptions = 5;
  const maxAnswers = 50;

  // Find out quiz options for the user, up to maxOptions
  for (let i = 1; i < maxOptions; i++) {
    if (!questionItem[`option_${i}_en`]) break;
    // Prioritize the 'alt' version of the option if available
    options.push(
      questionItem[`option_${i}_en_alt`] ?? questionItem[`option_${i}_en`],
    );
  }

  // Loop answers until undefined or reach maxAnswers
  let nextItemId = questionItem['next_script_id'];
  while (nextItemId && answers.length < maxAnswers) {
    const nextItem = Script.find((item) => item.id === nextItemId);
    if (!nextItem) break;
    // Prioritize the 'alt' version of the text if available
    answers.push(nextItem['text_en_alt'] ?? nextItem.text_en);
    nextItemId = nextItem['next_script_id'];
  }

  return { id, title, options, answers };
}
